import React, { useRef, useState } from 'react';
import {
    Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Image, Input, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalHeader, ModalOverlay, Table, Tbody, Td, Text, Th, Thead, Tr
} from '@chakra-ui/react';
import { Add, Remove, ZoomOutMap } from '@mui/icons-material';

import { lookupPincode } from '../../services/GeoServices';
import { whatsappLink } from '../../config/store';

// Product page building blocks (design-system.md §5.4)

// Desktop: photos stacked in a 2-column grid (one photo → a single column). Phone: swipe carousel with dots.
export const ProductGallery = ({ images, name, onZoom }) => {
    const [index, setIndex] = useState(0);
    const track = useRef();
    const onScroll = () => {
        const el = track.current;
        if (el) setIndex(Math.round(el.scrollLeft / el.clientWidth));
    };
    return (
        <>
            <Box display={{ base: 'none', md: 'grid' }} gridTemplateColumns={images.length > 1 ? '1fr 1fr' : '1fr'} gap='10px' maxWidth={images.length > 1 ? 'none' : '560px'} mx={images.length > 1 ? 0 : 'auto'} >
                {images.map((src, i) => (
                    <Box key={src + i} position='relative' bg='raano.imageBg' sx={{ aspectRatio: '2 / 3' }} role='group' >
                        <Image src={src} alt={`${name} - Image ${i + 1}`} width='100%' height='100%' objectFit='cover' loading={i ? 'lazy' : 'eager'} />
                        <Box as='button' aria-label='Zoom' position='absolute' top='14px' right='14px' width='36px' height='36px' borderRadius='full' bg='whiteAlpha.900' display='flex' alignItems='center' justifyContent='center' opacity={0} transition='opacity .2s' _groupHover={{ opacity: 1 }} _focusVisible={{ opacity: 1 }} onClick={() => onZoom(i)} >
                            <ZoomOutMap sx={{ fontSize: 18 }} />
                        </Box>
                    </Box>
                ))}
            </Box>
            <Box display={{ base: 'block', md: 'none' }} position='relative' >
                <Box ref={track} onScroll={onScroll} display='flex' overflowX='auto' sx={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }} >
                    {images.map((src, i) => (
                        <Box key={src + i} flex='0 0 100%' bg='raano.imageBg' sx={{ aspectRatio: '2 / 3', scrollSnapAlign: 'start' }} onClick={() => onZoom(i)} >
                            <Image src={src} alt={`${name} - Image ${i + 1}`} width='100%' height='100%' objectFit='cover' />
                        </Box>
                    ))}
                </Box>
                <Box as='button' aria-label='Zoom' position='absolute' top='12px' right='12px' color='raano.black' onClick={() => onZoom(index)} ><ZoomOutMap sx={{ fontSize: 22 }} /></Box>
                {images.length > 1 &&
                    <Box position='absolute' bottom='12px' left={0} right={0} display='flex' justifyContent='center' gap='6px' >
                        {images.map((_, i) => <Box key={i} width='7px' height='7px' borderRadius='full' border='1px solid' borderColor='raano.white' bg={i === index ? 'raano.white' : 'transparent'} />)}
                    </Box>}
            </Box>
        </>
    );
};

export const ZoomModal = ({ images, start, onClose, name }) => (
    <Modal isOpen={start !== null} onClose={onClose} size='full' >
        <ModalOverlay />
        <ModalContent bg='raano.white' >
            <ModalCloseButton zIndex={2} size='lg' />
            <ModalBody display='flex' alignItems='center' justifyContent='center' p={0} >
                {start !== null && <Image src={images[start]} alt={name} maxHeight='100vh' maxWidth='100%' objectFit='contain' />}
            </ModalBody>
        </ModalContent>
    </Modal>
);

// 136×44 (desktop) / 99×36 (phone) quantity stepper
export const QuantityStepper = ({ value, onChange, max = 10 }) => (
    <Box display='flex' alignItems='center' justifyContent='space-between' width={{ base: '99px', md: '136px' }} height={{ base: '36px', md: '43px' }} flexShrink={0} border='1px solid' borderColor='raano.qtyBorder' borderRadius='stepper' p='4px' >
        <Box as='button' type='button' aria-label='Decrease quantity' disabled={value <= 1} width={{ base: '26px', md: '33px' }} height='100%' borderRadius='3px' display='flex' alignItems='center' justifyContent='center' color={value <= 1 ? 'blackAlpha.300' : 'raano.black'} bg='raano.surface' onClick={() => onChange(Math.max(1, value - 1))} ><Remove sx={{ fontSize: 16 }} /></Box>
        <Text fontSize='14px' fontWeight={500} aria-live='polite' >{value}</Text>
        <Box as='button' type='button' aria-label='Increase quantity' disabled={value >= max} width={{ base: '26px', md: '33px' }} height='100%' borderRadius='3px' display='flex' alignItems='center' justifyContent='center' color='raano.white' bg='#6B7280' onClick={() => onChange(Math.min(max, value + 1))} ><Add sx={{ fontSize: 16 }} /></Box>
    </Box>
);

// "Check Estimated Delivery" pincode row, using the API's PIN code lookup
export const PincodeCheck = () => {
    const [pin, setPin] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const check = async (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(pin)) { setResult({ error: 'Enter a 6-digit PIN code.' }); return; }
        setLoading(true);
        try {
            const data = await lookupPincode(pin);
            setResult({ ok: `Delivers to ${data.city}, ${data.state}. Usually arrives in 3–5 days, with free delivery.` });
        } catch (err) {
            setResult({ error: err.response?.data?.error || 'We could not check this PIN code.' });
        }
        setLoading(false);
    };
    return (
        <Box as='form' onSubmit={check} mt={{ base: '24px', md: '32px' }} >
            <Text fontSize='14px' fontWeight={600} lineHeight='16px' mb='12px' >Check Estimated Delivery</Text>
            <Box display='flex' alignItems='center' height={{ base: '56px', md: '71px' }} border='1px solid' borderColor='blackAlpha.300' borderRadius='4px' pl='20px' >
                <Input variant='unstyled' inputMode='numeric' maxLength={6} placeholder='Enter your pincode' aria-label='PIN code' fontSize={{ base: '16px', md: '20px' }} value={pin} onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setResult(null); }} />
                <Box as='button' type='submit' px='19.2px' height='100%' fontSize={{ base: '16px', md: '18px' }} fontWeight={600} letterSpacing='0.02em' opacity={loading ? 0.5 : 1} >CHECK</Box>
            </Box>
            {result && <Text mt='8px' fontSize='13px' color={result.error ? 'raano.error' : 'raano.success'} >{result.error || result.ok}</Text>}
        </Box>
    );
};

// Approximate garment measurements in inches. Replace with Raano's own measured chart.
const TOPS = [['XS', 36, 26], ['S', 38, 27], ['M', 40, 28], ['L', 42, 29], ['XL', 44, 30], ['XXL', 46, 31], ['3XL', 48, 32]];
const WAIST = [['28', 28, 40], ['30', 30, 40.5], ['32', 32, 41], ['34', 34, 41.5], ['36', 36, 42], ['38', 38, 42.5], ['40', 40, 43]];

export const SizeChartModal = ({ isOpen, onClose, sizes = [] }) => {
    const waist = sizes.some(s => /^\d+$/.test(s));
    const rows = (waist ? WAIST : TOPS).filter(r => !sizes.length || sizes.includes(r[0]));
    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size='md' motionPreset='scale' >
            <ModalOverlay />
            <ModalContent borderRadius='8px' mx='12px' >
                <ModalHeader fontSize='18px' fontWeight={700} sx={{ fontStretch: '75%' }} textTransform='uppercase' >Size chart</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb='20px' >
                    <Table size='sm' >
                        <Thead><Tr><Th>Size</Th><Th isNumeric >{waist ? 'Waist (in)' : 'Chest (in)'}</Th><Th isNumeric >{waist ? 'Length (in)' : 'Length (in)'}</Th></Tr></Thead>
                        <Tbody>{rows.map(([s, a, b]) => <Tr key={s} ><Td fontWeight={600} >{s}</Td><Td isNumeric >{a}</Td><Td isNumeric >{b}</Td></Tr>)}</Tbody>
                    </Table>
                    <Text mt='14px' fontSize='13px' color='raano.muted' >
                        Approximate garment measurements. Between sizes? <Box as='a' href={whatsappLink('Hi Raano, can you help me with sizing?')} target='_blank' rel='noreferrer' textDecoration='underline' >Ask us on WhatsApp</Box>.
                    </Text>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

// Product Details · Delivery & Returns · More Information
export const PdpAccordions = ({ sections }) => (
    <Accordion allowMultiple mt={{ base: '20px', md: '28px' }} >
        {sections.map(s => (
            <AccordionItem key={s.title} borderTop='none' borderBottom='1px solid' borderColor='raano.border' >
                {({ isExpanded }) => (
                    <>
                        <AccordionButton px={0} pt='20px' pb='11.2px' _hover={{ bg: 'transparent' }} >
                            <Text as='h2' flex={1} textAlign='left' fontSize='14px' fontWeight={500} lineHeight='21px' >{s.title}</Text>
                            {isExpanded ? <Remove sx={{ fontSize: 18 }} /> : <Add sx={{ fontSize: 18 }} />}
                        </AccordionButton>
                        <AccordionPanel px={0} pt='4px' pb='18px' fontSize='14px' lineHeight='1.6' color='blackAlpha.800' >{s.body}</AccordionPanel>
                    </>
                )}
            </AccordionItem>
        ))}
    </Accordion>
);
